import { Component, OnInit, OnDestroy, Input, Output, ViewChild, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { NgForm } from '@angular/forms';

import { Subscription, Observable } from 'rxjs';
import { first, map, switchMap, debounceTime, take, } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { UID } from '@saliente/library';

import { PartnerModel } from './partners.models';
import { CommonService, } from '../general/common.service';
import { AuthService, } from '../auth/auth.service';
import { ConfigService, } from '../general/config.service';
import { PartnersService } from './partners.service';
import { DictionaryCommonService } from 'src/app/common/general/dictionary-common.service';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { SysSaftPartnerPrefixModel } from 'src/app/clients/clientpartners/clientpartner.models';

@Component({
    selector: 'partner-edit-form',
    templateUrl: 'partner-edit-form.component.html',
    styleUrls: ['./partner-edit-form.component.scss'],
    standalone: false
})
export class PartnerEditFormComponent implements OnInit, OnDestroy {
  @Output() public save = new EventEmitter();
  @Output() public close = new EventEmitter();

  private _partnerComponent: any;
  private partnerComponentFilterChangeSubscription: Subscription;
  @ViewChild('partner') get partnerComponent(): any {
    return this._partnerComponent;
  }
  set partnerComponent(value: any) {
    this.unhookPartnerComponent();
    this._partnerComponent = value;
    if (this._partnerComponent) {
      this.hookPartnerComponent();
    }
  }

  private _personPinComponent: any;
  private personPinComponentFilterChangeSubscription: Subscription;
  @ViewChild('personPin') get personPinComponent(): any {
    return this._personPinComponent;
  }
  set personPinComponent(value: any) {
    this.unhookPersonPinComponent();
    this._personPinComponent = value;
    if (this._personPinComponent) {
      this.hookPersonPinComponent();
    }
  }

  private _model: PartnerModel;
  @Input() public get model(): PartnerModel {
    return this._model;
  }
  public set model(value: PartnerModel) {
    this._model = value;
    if (this._model && this._model.fiscalNumber && !this._model.partnerName) {
      if (this.isLocalPartner && this._model.isLegalPerson) {
        this.fetchCompanyData();
      }
    }
  }

  public countryList: any[];
  public countryListFilteredData: any[];
  public partnerTypes: any[] = [];
  private suspendResetPartnerList: boolean = false;
  public partners: any[] = [];
  public countyDictionary: any[];
	public countyFilteredData: any[];
  public cityDictionary: any[];
	public cityFilteredData: any[];

	public saftPrefixList: SysSaftPartnerPrefixModel[] = [];

  get isLocalPartner(): boolean {
    return this._model && (this._model.countryCode === this.configService.localCountryCode);
  }

  get isEUPartner(): boolean {
    return this._model && (this._model.countryCode === this.configService.localCountryCode || this._model.region == 'EU');
  }

  get partnerEditDisabled() {
    return this.isLocalPartner && this._model.isLegalPerson && !this.partnersService.companyDataServiceIsDown;
  }

  get isSaftPrefixVisible(): boolean {
		return this.authService.isInRole("EXPERT") && this.model && this.model.partnerType != null &&
			this.model.countryCode === this.configService.localCountryCode &&
			this.model.fiscalNumber?.length == 13 && this.model.fiscalNumber.startsWith("90000");
	}

  constructor(private commonService: CommonService, private configService: ConfigService, private authService: AuthService, private partnersService: PartnersService,
    private cdr: ChangeDetectorRef, private toastr: ToastrService, private dictionaryCommonService: DictionaryCommonService) {
      this.commonService.partnerTypeListStream().pipe(first())
        .subscribe((partnerTypes) => {
          this.partnerTypes = partnerTypes;
        });
      this.commonService.countryList().pipe(first())
        .subscribe((response: any[]) => {
          this.countryList = response;
          this.countryListFilteredData = this.countryList.slice();
        });
      this.commonService.getSysSaftPartnerPrefixDictionary().pipe(first())
        .subscribe((saftPrefixList) => {
          this.saftPrefixList = saftPrefixList;
        });
      
      /* AM. 15.05.2024. task 7706: pending clarification: dictionary values vs ANAF values (diacritics)
      this.dictionaryCommonService.getDictionary(this.configService.accountsUrl, 'county').pipe(take(1))
				.subscribe((result: any[]) => {
					if (result) {
						this.countyDictionary = result;
						this.countyFilteredData = this.countyDictionary.slice();
					}
				});
      this.dictionaryCommonService.getDictionary(this.configService.accountsUrl, 'city').pipe(take(1))
				.subscribe((result: any[]) => {
					if (result) {
						this.cityDictionary = result;
						this.cityFilteredData = this.cityDictionary.slice();
          }
        });
      */
  }

  public ngOnInit() { }

  public ngOnDestroy() {
    this.unhookPartnerComponent();
    this.unhookPersonPinComponent();
  }

  public onCountryChange(dataItem: any) {
    let countryCode = this._model.countryCode;
    if (dataItem) {
      this._model.countryName = dataItem.countryName;
      this._model.countryCode = dataItem.countryCode;
      this._model.region = dataItem.region;
    } else {
      this._model.countryName = null;
      this._model.countryCode = null;
      this._model.region = null;
    }

    if (this._model.countryCode != countryCode) {
      if (this._model.isLegalPerson) {
        this.selectedPartner = null;
      } else {
        this._model.county = null;
        this._model.city = null;
        this._model.addressDetails = null;
      }
    }
  }


  private _selectedPartner: any;
  get selectedPartner(): any {
    if (!this._selectedPartner) {
      this._selectedPartner = this.modelToPlainPartner();
    }
    return this._selectedPartner;
  }
  set selectedPartner(value: any) {
    if (value && value.eid) {
      this.copyPartnerFields(value);
    } else {
      this.clearPartnerFields();
      value = null;
    }
    this._selectedPartner = value;
    if (!value && this._partnerComponent && this._partnerComponent.value != value) {
      setTimeout(() => { this._partnerComponent.value = value; }, 0);
    }
  }

  private modelToPlainPartner(): any {
    if (this._model) {
      return {
        eid: this._model.id,
        name: this._model.partnerName || '',
        identificationNumber: this._model.fiscalNumber || '',
        registrationNumber: this._model.registrationNumber || '',
        display: this.partnersService.partnerDisplay(this._model.fiscalNumber || '', this._model.partnerName || ''),
        countryCode: this._model.countryCode,
        countryName: this._model.countryName,
        region: this._model.region,
        countyName: this._model.county,
        cityName: this._model.city,
        addressDetails: this._model.addressDetails,
        taxAttribute: this._model.taxAttribute,
        //taxCode: this._model.taxCode,
      };
    }
    return null;
  }

  private clearPartnerFields() {
    this._model.partnerName = null;
    this._model.fiscalNumber = null;
    this._model.registrationNumber = null;
    this._model.county = null;
    this._model.city = null;
    this._model.addressDetails = null;
    this._model.taxAttribute = null;
    //this._model.taxCode = null;
    this._model.id = UID();
  }

  private copyPartnerFields(dataItem: any) {
    if (dataItem) {
      try {
        this.suspendResetPartnerList = true;
        this._model.partnerName = dataItem.name;
        this._model.fiscalNumber = dataItem.identificationNumber;
        this._model.registrationNumber = dataItem.registrationNumber;
        this._model.countryCode = dataItem.countryCode;
        this._model.countryName = dataItem.countryName;
        this._model.region = dataItem.region;
        this._model.county = dataItem.countyName;
        this._model.city = dataItem.cityName;
        this._model.addressDetails = dataItem.addressDetails;
        this._model.taxAttribute = dataItem.taxAttribute;
        //this._model.taxCode = dataItem.taxCode;
        this._model.id = dataItem.eid;

      } finally {
        this.suspendResetPartnerList = false;
      }
    } else {
      this.clearPartnerFields();
    }
  }

  private resetPartnerList() {
    if (!this.suspendResetPartnerList) {
      this._selectedPartner = null;
      const selectedPartner = this.selectedPartner;
      if (selectedPartner) {
        this.partners = [selectedPartner];
      } else {
        this.partners = [];
      }
    }
  }

  private _selectedPartnerByFiscalNumberNormalizer: any;
  public selectedPartnerByFiscalNumberNormalizer() {
    const self = this;
    if (!this._selectedPartnerByFiscalNumberNormalizer) {
      this._selectedPartnerByFiscalNumberNormalizer = (text: Observable<string>) => {
        return text.pipe(
          map((text: string) => {
            if (this.isLocalPartner && this._model.isLegalPerson) {
              this.fetchCompanyData(text);
            }
            const result = this.modelToPlainPartner();
            result.eid = UID();
            result.identificationNumber = text;
            return result;
          })
        );
      }
    }
    return this._selectedPartnerByFiscalNumberNormalizer;
  }

  private _selectedPartnerByPartnerNameNormalizer: any;
  public selectedPartnerByPartnerNameNormalizer() {
    const self = this;
    if (!this._selectedPartnerByPartnerNameNormalizer) {
      this._selectedPartnerByPartnerNameNormalizer = (text: Observable<string>) => {
        return text.pipe(
          map((text: string) => {
            const result = this.modelToPlainPartner();
            result.eid = UID();
            result.name = text;
            return result;
          })
        );
      }
    }
    return this._selectedPartnerByPartnerNameNormalizer;
  }

  private partnerFilterSubscription(component: any) {
    return component.filterChange
      .asObservable()
      .pipe(
        debounceTime(300),
        switchMap((filter: string) => {
          const params: any = { filter, count: 100 };
          if (this._model.isLegalPerson) {
            params.filterNew = 'isLegalPerson: true';
          } else {
            params.filterNew = 'isLegalPerson: false';
            params.clientEid = this.authService.user.selectedCompanyId
          }
          component.loading = true;
          return this.partnersService
            .partnerListStream(params, { spinner: false })
            .pipe(first());
        })
      )
      .subscribe((result: any[]) => {
        this.partners = result;
        component.loading = false;
      });
  }

  private hookPartnerComponent() {
    this.partnerComponentFilterChangeSubscription = this.partnerFilterSubscription(this.partnerComponent);
  }

  private unhookPartnerComponent() {
    if (this.partnerComponentFilterChangeSubscription) {
      this.partnerComponentFilterChangeSubscription.unsubscribe();
      this.partnerComponentFilterChangeSubscription = null;
    }
  }

  private hookPersonPinComponent() {
    this.personPinComponentFilterChangeSubscription = this.partnerFilterSubscription(this.personPinComponent);
  }

  private unhookPersonPinComponent() {
    if (this.personPinComponentFilterChangeSubscription) {
      this.personPinComponentFilterChangeSubscription.unsubscribe();
      this.personPinComponentFilterChangeSubscription = null;
    }
  }

  public fetchCompanyData(fiscalNumber?: string) {
    fiscalNumber = fiscalNumber || this._model.fiscalNumber;
    if (fiscalNumber) {
      if (fiscalNumber.toLowerCase().startsWith('ro')) {
        fiscalNumber = fiscalNumber.substr(2);
      }
      this.partnersService.companyDataStream(fiscalNumber)
        .pipe(first())
        .subscribe((companyData: any) => {
          if (companyData) {
            this._model.partnerName = companyData.companyName;
            this._model.registrationNumber = companyData.comercialRegistryNo;
            this._model.county = companyData.county;
            this._model.city = companyData.city;
            this._model.addressDetails = companyData.details;
            this._model.taxAttribute = companyData.isVATPayer ? 'RO' : null;
            this._model.fiscalNumber = companyData.fiscalNo;
            this.detectChanges();
          } else {
            this.clearPartnerFields();
            this._model.fiscalNumber = fiscalNumber;
          }
          this._selectedPartner = null;
        });
    }
  }
  public fetchEUCompanyData(fiscalNumber: string, countryCode: string) {
    if (countryCode.toLowerCase() === 'ro') {
      this.fetchCompanyData(fiscalNumber);
      return;
    }
    this.partnersService.fetchVIESStream(fiscalNumber).subscribe(data => {
      this.clearPartnerFields();
      if (data.accounts.getViesVatDataUsingGET.found) {
        this._model.partnerName = data.accounts.getViesVatDataUsingGET.name;
        this._model.addressDetails = data.accounts.getViesVatDataUsingGET.address;
        this._model.fiscalNumber = fiscalNumber;
        this.detectChanges();
      } else {

        this._model.fiscalNumber = fiscalNumber;
      }
      this._selectedPartner = null;

    });

  }

  public onSave(partnerForm: NgForm) {
    if (partnerForm.valid) {
      if (this._model.isLegalPerson && !(/^[a-zA-Z0-9]{3,}$/i.test(this.model.fiscalNumber))) {
        this.toastr.error('CUI-ul este invalid. Te rog sa completezi minim 3 caractere să conțină cel puțin 3 caractere/cifre, fără caractere speciale: spațiu, -, etc.');
        return;
      }

      this.save.emit(partnerForm)
    }
  }

  private detectChangesTimeoutId: any;
  public detectChanges() {
    if (this.detectChangesTimeoutId) {
      clearTimeout(this.detectChangesTimeoutId);
    }
    this.detectChangesTimeoutId = setTimeout(() => {
      this.cdr.detectChanges();
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 10);
    }, 10);
  }

  public validateCNPValue(value: any) {
    if (value && value.identificationNumber) {
      if (!(/^[a-zA-Z0-9]{5,}/g.test(value.identificationNumber))) {
        return 'CNP trebuie să fie minim 5 caractere și să nu conțină caractere speciale (spațiu, - etc.)!'
      }
    }

    return true;
	}

  public handleCountryFilter(value: any) {
		if (this.countryList != undefined) {
			this.countryListFilteredData = this.countryList.filter((c) => c.countryName.toLowerCase().includes(value.toLowerCase()));
		}
	}

	/*public handleCountyFilter(value: any) {
		if (this.countyDictionary != undefined) {
			this.countyFilteredData = this.countyDictionary.filter((c) => c.countyName.toLowerCase().includes(value.toLowerCase()));
		}
	}

	public handleCityFilter(value: any) {
		if (this.cityDictionary != undefined) {
			this.cityFilteredData = this.cityDictionary.filter((c) => c.cityName.toLowerCase().includes(value.toLowerCase()));
		}
	}*/
}
